import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AuthContext } from '../context/AuthContext';
import axios from '../api/axiosConfig';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import * as XLSX from 'xlsx';
import './Profiles.css';

const RangeRequests = () => {
  const { user } = useContext(AuthContext);
  const [requests, setRequests] = useState([]);
  const [transitos, setTransitos] = useState([]);
  const [availablePlates, setAvailablePlates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filterStatus, setFilterStatus] = useState('todos');
  const [filterApplicant, setFilterApplicant] = useState('todos');

  // Modales
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPlatesModal, setShowPlatesModal] = useState(false);
  
  // Detalle de placas asignadas
  const [assignedPlates, setAssignedPlates] = useState([]);
  const [loadingPlates, setLoadingPlates] = useState(false);

  // Formulario Nueva Solicitud
  const [requestForm, setRequestForm] = useState({
    transito_id: '',
    vehicle_type: 'carro',
    requested_quantity: '',
    comments: ''
  });

  // Solicitud seleccionada para aprobar/rechazar
  const [selectedRequest, setSelectedRequest] = useState(null);

  // Formulario Aprobación
  const [approveForm, setApproveForm] = useState({
    mode: 'existente', // 'existente' o 'nuevo'
    prefix: '',
    start_number: '',
    end_number: '',
    suffix: '',
    start_plate: '',
    end_plate: ''
  });

  // Formulario Rechazo
  const [rejectionReason, setRejectionReason] = useState('');

  // Sub-rangos fraccionados (modo existente)
  const [subRanges, setSubRanges] = useState([{ start_plate: '', end_plate: '' }]);

  // Sub-rangos fraccionados para crear nuevo rango (modo nuevo)
  const [newSubRanges, setNewSubRanges] = useState([{ prefix: '', start_number: '', end_number: '', suffix: '' }]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [requestsRes, transitosRes] = await Promise.all([
        axios.get('/api/range-requests'),
        axios.get('/api/transitos')
      ]);

      setRequests(requestsRes.data);
      setTransitos(transitosRes.data.filter(t => t.status === 'activo'));
      setError('');
    } catch (err) {
      setError('Error al cargar datos de solicitudes de rangos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cargar placas disponibles cuando se va a aprobar con placas existentes
  const fetchAvailablePlates = async (transitoId, vehicleType) => {
    try {
      const res = await axios.get(`/api/transitos/${transitoId}/plates?vehicle_type=${vehicleType}`);
      // Filtrar solo las disponibles y que no pertenezcan a ningún concesionario
      const libres = res.data.filter(p => p.status === 'disponible' && !p.concesionario_id);
      setAvailablePlates(libres);
    } catch (err) {
      console.error('Error al cargar placas disponibles:', err);
    }
  };

  // -- Helpers sub-rangos --
  const addSubRange = () => setSubRanges(prev => [...prev, { start_plate: '', end_plate: '' }]);

  const removeSubRange = (idx) => setSubRanges(prev => prev.filter((_, i) => i !== idx));

  const updateSubRange = (idx, field, value) => {
    setSubRanges(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value, ...(field === 'start_plate' ? { end_plate: '' } : {}) } : r));
  };

  // Placas cubiertas por un sub-rango específico
  const getSubRangeCount = (sr) => {
    if (!sr.start_plate || !sr.end_plate) return 0;
    return availablePlates.filter(p => p.plate_number >= sr.start_plate && p.plate_number <= sr.end_plate).length;
  };

  // Total de placas cubiertas por todos los sub-rangos
  const getTotalCovered = () => subRanges.reduce((acc, sr) => acc + getSubRangeCount(sr), 0);

  // -- Helpers sub-rangos para nuevo rango (modo nuevo) --
  const addNewSubRange = () => setNewSubRanges(prev => [...prev, { prefix: '', start_number: '', end_number: '', suffix: '' }]);
  const removeNewSubRange = (idx) => setNewSubRanges(prev => prev.filter((_, i) => i !== idx));
  const updateNewSubRange = (idx, field, value) => {
    setNewSubRanges(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };
  const getSubRangeNewCount = (sr) => {
    const start = parseInt(sr.start_number);
    const end = parseInt(sr.end_number);
    if (isNaN(start) || isNaN(end) || end < start) return 0;
    return end - start + 1;
  };
  const getTotalNewCovered = () => newSubRanges.reduce((acc, sr) => acc + getSubRangeNewCount(sr), 0);

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    if (!requestForm.transito_id) {
      setError('Debe seleccionar el Organismo de Tránsito para la solicitud de rango.');
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const payload = { ...requestForm };
      await axios.post('/api/range-requests', payload);
      setSuccess('Solicitud creada correctamente');
      fetchData();
      setShowRequestModal(false);
      setRequestForm({ transito_id: '', vehicle_type: 'carro', requested_quantity: '', comments: '' });
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear solicitud');
    }
  };

  const handleOpenApproveModal = (reqItem) => {
    setSelectedRequest(reqItem);
    // Configurar modo inicial: si no es concesionario (solicitud global de TransiApp), forzar modo 'nuevo'
    const initialMode = reqItem.concesionario_id ? 'existente' : 'nuevo';
    setApproveForm({
      mode: initialMode,
      prefix: '',
      start_number: '',
      end_number: '',
      suffix: '',
      start_plate: '',
      end_plate: '',
      transito_id: reqItem.transito_id || ''
    });
    setSubRanges([{ start_plate: '', end_plate: '' }]);
    setNewSubRanges([{ prefix: '', start_number: '', end_number: '', suffix: '' }]);
    if (reqItem.transito_id) {
      fetchAvailablePlates(reqItem.transito_id, reqItem.vehicle_type);
    } else {
      setAvailablePlates([]);
    }
    setShowApproveModal(true);
  };

  const handleApprove = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      const payload = {
        create_range: approveForm.mode === 'nuevo',
        transito_id: approveForm.transito_id || selectedRequest.transito_id
      };

      if (approveForm.mode === 'nuevo') {
        const validNewRanges = newSubRanges.filter(r => r.prefix && r.start_number !== '' && r.end_number !== '');
        if (validNewRanges.length === 0) {
          setError('Debe configurar al menos un rango nuevo válido con prefijo, número inicial y final.');
          return;
        }
        payload.new_ranges = validNewRanges;
        // Compatibilidad hacia atrás si solo hay un rango
        payload.prefix = validNewRanges[0].prefix;
        payload.start_number = parseInt(validNewRanges[0].start_number);
        payload.end_number = parseInt(validNewRanges[0].end_number);
        payload.suffix = validNewRanges[0].suffix;
      } else {
        const validSubRanges = subRanges.filter(r => r.start_plate && r.end_plate);
        if (validSubRanges.length === 0) {
          setError('Debe configurar al menos un sub-rango válido con placa inicial y final.');
          return;
        }
        payload.sub_ranges = validSubRanges;
      }

      const res = await axios.put(`/api/range-requests/${selectedRequest.id}/approve`, payload);
      setSuccess(res.data.message || 'Solicitud aprobada con éxito');
      fetchData();
      setShowApproveModal(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al aprobar solicitud');
    }
  };

  const handleOpenRejectModal = (reqItem) => {
    setSelectedRequest(reqItem);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const handleReject = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      const res = await axios.put(`/api/range-requests/${selectedRequest.id}/reject`, {
        rejection_reason: rejectionReason
      });
      setSuccess(res.data.message || 'Solicitud rechazada');
      fetchData();
      setShowRejectModal(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al rechazar solicitud');
    }
  };

  // Descarga en Excel las placas asignadas a una solicitud
  const handleDownloadPlatesExcel = async (reqItem, platesCache = null) => {
    try {
      const plates = platesCache || (await axios.get(`/api/range-requests/${reqItem.id}/plates`)).data;
      if (!plates || plates.length === 0) {
        setError(`La solicitud #${reqItem.id} no tiene placas asignadas para descargar.`);
        return;
      }
      const tipo = reqItem.vehicle_type === 'moto' ? 'Moto' : reqItem.vehicle_type === 'moto_carro' ? 'Moto Carro' : 'Carro';
      const rows = plates.map((p, i) => ({
        '#': i + 1,
        'Placa': p.plate_number,
        'Estado': p.status === 'disponible' ? 'Disponible' : p.status === 'asignada' ? 'Asignada' : p.status,
        'Tipo de Vehículo': tipo,
        'Tránsito': reqItem.transito_name || '',
        'Concesionario': p.concesionario_name || reqItem.concesionario_name || 'TransiApp (Global)',
        'Solicitud': reqItem.id,
        'Fecha Asignación': p.assigned_to_concesionario_at ? new Date(p.assigned_to_concesionario_at).toLocaleString('es-CO') : ''
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 5 }, { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 18 }, { wch: 30 }, { wch: 10 }, { wch: 22 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Placas');
      XLSX.writeFile(wb, `placas_solicitud_${reqItem.id}_${(reqItem.transito_name || '').replace(/\s+/g, '_')}.xlsx`);
    } catch (err) {
      console.error(err);
      setError('Error al descargar el Excel de placas');
    }
  };

  const handleFetchPlatesDetail = async (reqItem) => {
    setSelectedRequest(reqItem);
    setAssignedPlates([]);
    setLoadingPlates(true);
    setShowPlatesModal(true);
    try {
      const res = await axios.get(`/api/range-requests/${reqItem.id}/plates`);
      setAssignedPlates(res.data);
    } catch (err) {
      console.error(err);
      setError('Error al obtener el detalle de las placas');
    } finally {
      setLoadingPlates(false);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!window.confirm('¿Está seguro de eliminar esta solicitud de rango?\n\nADVERTENCIA: Esta acción eliminará la solicitud e intentará deshacer toda vinculación de placas en el inventario o borrar el rango de pruebas creado.')) {
      return;
    }
    try {
      setError('');
      setSuccess('');
      const res = await axios.delete(`/api/range-requests/${requestId}`);
      setSuccess(res.data.message || 'Solicitud y vinculaciones eliminadas con éxito');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Error al eliminar la solicitud');
    }
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [filterStatus, filterApplicant]);

  const uniqueApplicants = Array.from(
    new Set(requests.map(r => r.creator_name).filter(Boolean))
  ).sort();

  // Filtrar solicitudes
  const filteredRequests = requests.filter(r => {
    const matchesStatus = filterStatus === 'todos' || r.status === filterStatus;
    const matchesApplicant = filterApplicant === 'todos' || r.creator_name === filterApplicant;
    return matchesStatus && matchesApplicant;
  });

  // Paginación de solicitudes
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  // Cantidad de placas seleccionadas en stock existente
  const getSelectedRangeCount = () => {
    if (!approveForm.start_plate || !approveForm.end_plate) return 0;
    return availablePlates.filter(
      p => p.plate_number >= approveForm.start_plate && p.plate_number <= approveForm.end_plate
    ).length;
  };

  // Estilos de estado
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pendiente': return 'status inactivo'; // Amarillo/Gris
      case 'en_proceso_transito': return 'status pendiente'; // Azul/Púrpura
      case 'asignado': return 'status activo'; // Verde
      case 'rechazado': return 'status bloqueado'; // Rojo
      default: return 'status';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pendiente': return '⏳ Pendiente TransiApp';
      case 'en_proceso_transito': return '🏢 En Tránsito';
      case 'asignado': return '✅ Asignado';
      case 'rechazado': return '❌ Rechazado';
      default: return status;
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Navbar title="📋 Solicitudes de Asignación de Rangos" />

        <div className="page-content">
          {error && <div className="error-banner">{error}</div>}
          {success && <div className="success-banner" style={{ padding: '12px', marginBottom: '20px', background: '#ecfdf5', color: '#065f46', borderRadius: '8px', border: '1px solid #a7f3d0', fontWeight: '500' }}>{success}</div>}

          <div className="page-header">
            <div className="header-title">
              <h2>Historial de Solicitudes</h2>
              <p className="subtitle">Gestión y control de asignaciones de rangos entre Puntos de Ventas, TransiApp y Tránsitos</p>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <select 
                value={filterStatus} 
                onChange={(e) => setFilterStatus(e.target.value)}
                className="btn-secondary"
                style={{ padding: '8px 16px', borderRadius: '8px' }}
              >
                <option value="todos">Todos los estados</option>
                <option value="pendiente">⏳ Pendientes TransiApp</option>
                <option value="en_proceso_transito">🏢 En Tránsito</option>
                <option value="asignado">✅ Asignados</option>
                <option value="rechazado">❌ Rechazados</option>
              </select>

              <select 
                value={filterApplicant} 
                onChange={(e) => setFilterApplicant(e.target.value)}
                className="btn-secondary"
                style={{ padding: '8px 16px', borderRadius: '8px', background: '#fff' }}
              >
                <option value="todos">👤 Todos los solicitantes</option>
                {uniqueApplicants.map(app => (
                  <option key={app} value={app}>👤 {app}</option>
                ))}
              </select>

              {!user?.transito_id && user?.profile_name !== 'admin_transito' && user?.role !== 'admin_transito' && user?.profile_name !== 'administrativo_concesionario' && (
                <button onClick={() => setShowRequestModal(true)} className="btn-primary">
                  ➕ Solicitar Rango
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <p className="loading">Cargando solicitudes...</p>
          ) : (
            <div className="table-wrapper">
              <div className="table-responsive">
                <table className="users-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Solicitante (Punto Venta)</th>
                    <th>Tránsito</th>
                    <th>Tipo Vehículo</th>
                    <th>Cant. Solicitada</th>
                    <th>Cant. Asignada</th>
                    <th>Estado</th>
                    <th>Fecha Creación</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(r => (
                    <tr key={r.id}>
                      <td><strong>#{r.id}</strong></td>
                      <td>
                        {r.concesionario_name ? (
                          <span style={{ fontWeight: '600', color: '#1e3a8a' }}>{r.concesionario_name}</span>
                        ) : (
                          <span style={{ fontStyle: 'italic', color: '#64748b' }}>TransiApp (Global)</span>
                        )}
                        <div style={{ fontSize: '11px', color: '#64748b' }}>por {r.creator_name}</div>
                      </td>
                       <td>{r.transito_name || <span style={{ fontStyle: 'italic', color: '#94a3b8' }}>— Por asignar —</span>}</td>
                      <td>
                        <span className={`vehicle-type-badge ${r.vehicle_type}`}>
                          {r.vehicle_type === 'moto' ? '🏍️ Moto' : 
                           r.vehicle_type === 'moto_carro' ? '🛺 Moto Carro' : 
                           '🚗 Carro'}
                        </span>
                      </td>
                      <td>
                        <span className="plate-badge-sm" style={{ background: '#f1f5f9', color: '#334155', fontWeight: '800', fontSize: '14px' }}>
                          {r.requested_quantity}
                        </span>
                      </td>
                      <td>
                        {r.status === 'asignado' ? (
                          <span className="plate-badge-sm" style={{ background: '#dcfce7', color: '#15803d', fontWeight: '800', fontSize: '14px', border: '1px solid #bbf7d0' }}>
                            {r.assigned_quantity !== null && r.assigned_quantity !== undefined ? r.assigned_quantity : (r.requested_quantity || '—')}
                          </span>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '13px', fontStyle: 'italic' }}>—</span>
                        )}
                      </td>
                      <td>
                        <span className={getStatusBadgeClass(r.status)}>
                          {getStatusLabel(r.status)}
                        </span>
                      </td>
                      <td>{new Date(r.created_at).toLocaleString()}</td>
                      <td className="actions">
                        {/* Aprobación/Rechazo de Solicitud de Concesionario (Gestionado por TransiApp) */}
                        {(r.status === 'pendiente' || r.status === 'en_proceso_transito') && r.concesionario_id && 
                          (user?.role === 'superadmin' || (user?.role === 'admin' && !user?.transito_id && !user?.concesionario_id)) && (
                            <>
                              <button 
                                onClick={() => handleOpenApproveModal(r)} 
                                className="btn-action btn-edit"
                                title="Aprobar Solicitud de Concesionario"
                              >
                                ✅ Aprobar
                              </button>
                              <button 
                                onClick={() => handleOpenRejectModal(r)} 
                                className="btn-action btn-delete"
                                title="Rechazar Solicitud de Concesionario"
                              >
                                ❌ Rechazar
                              </button>
                            </>
                        )}

                        {/* Aprobación/Rechazo de Solicitud de TransiApp a Tránsito (Gestionado EXCLUSIVAMENTE por el Tránsito destino) */}
                        {(r.status === 'pendiente' || r.status === 'en_proceso_transito') && !r.concesionario_id && 
                          (user?.transito_id && parseInt(user?.transito_id) === parseInt(r.transito_id)) && (
                            <>
                              <button 
                                onClick={() => handleOpenApproveModal(r)} 
                                className="btn-action btn-edit"
                                title="Aprobar Solicitud de TransiApp"
                              >
                                ✅ Aprobar
                              </button>
                              <button 
                                onClick={() => handleOpenRejectModal(r)} 
                                className="btn-action btn-delete"
                                title="Rechazar Solicitud de TransiApp"
                              >
                                ❌ Rechazar
                              </button>
                            </>
                        )}

                        {r.status === 'asignado' && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', alignItems: 'center' }}>
                            <span style={{ fontSize: '12px', color: '#16a34a', fontWeight: '600' }}>
                              Rango #{r.assigned_range_id || 'N/A'} Asignado
                            </span>
                            <button
                              onClick={() => handleFetchPlatesDetail(r)}
                              className="btn-action btn-view"
                              style={{ padding: '4px 8px', fontSize: '11px', whiteSpace: 'nowrap' }}
                              title="Ver detalle de placas asignadas"
                            >
                              👁️ Ver Placas
                            </button>
                            <button
                              onClick={() => handleDownloadPlatesExcel(r)}
                              className="btn-action"
                              style={{ padding: '4px 8px', fontSize: '11px', whiteSpace: 'nowrap', background: '#16a34a', color: '#fff', border: 'none' }}
                              title="Descargar Excel con las placas asignadas"
                            >
                              📥 Excel
                            </button>
                          </div>
                        )}

                        {r.status === 'rechazado' && (
                          <div style={{ fontSize: '11px', color: '#dc2626', maxWidth: '180px', wordBreak: 'break-word' }}>
                            <strong>Motivo: </strong> {r.rejection_reason}
                          </div>
                        )}

                        {user?.role === 'superadmin' && (
                          <button
                            onClick={() => handleDeleteRequest(r.id)}
                            className="btn-action"
                            style={{ 
                              background: 'rgba(239, 68, 68, 0.1)', 
                              color: '#ef4444', 
                              border: '1px solid rgba(239, 68, 68, 0.2)',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              marginLeft: '5px',
                              transition: 'all 0.2s'
                            }}
                            title="Eliminar solicitud de pruebas y deshacer vinculación"
                          >
                            🗑️
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {filteredRequests.length === 0 && (
                <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                  No se encontraron solicitudes de rangos.
                </div>
              )}

              <Pagination 
                currentPage={currentPage}
                totalItems={filteredRequests.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          )}
        </div>
      </div>

      {/* MODAL NUEVA SOLICITUD */}
      {showRequestModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <h3>Solicitar Rango de Placas</h3>
            <form onSubmit={handleCreateRequest}>
              <div className="form-group">
                <label>Organismo de Tránsito *</label>
                <select 
                  value={requestForm.transito_id} 
                  onChange={(e) => setRequestForm({...requestForm, transito_id: e.target.value})}
                  required
                >
                  <option value="">Seleccione tránsito...</option>
                  {transitos.map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Tipo de Vehículo</label>
                <select 
                  value={requestForm.vehicle_type} 
                  onChange={(e) => setRequestForm({...requestForm, vehicle_type: e.target.value})}
                  required
                >
                  <option value="carro">🚗 Carro</option>
                  <option value="moto">🏍️ Moto</option>
                  <option value="moto_carro">🛺 Moto Carro</option>
                </select>
              </div>

              <div className="form-group">
                <label>Cantidad Solicitada</label>
                <input 
                  type="number" 
                  min="1" 
                  max="1000"
                  placeholder="Ej: 50"
                  value={requestForm.requested_quantity}
                  onChange={(e) => setRequestForm({...requestForm, requested_quantity: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Comentarios / Observaciones</label>
                <textarea 
                  rows="3"
                  placeholder="Escribe alguna observación o justificación..."
                  value={requestForm.comments}
                  onChange={(e) => setRequestForm({...requestForm, comments: e.target.value})}
                ></textarea>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => setShowRequestModal(false)} 
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Enviar Solicitud
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL APROBAR SOLICITUD */}
      {showApproveModal && selectedRequest && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '550px' }}>
            <h3>Aprobar Solicitud #{selectedRequest.id}</h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '15px' }}>
              <strong>Solicitante: </strong> {selectedRequest.concesionario_name || 'TransiApp (Global)'} <br />
              <strong>Cantidad Requerida: </strong> {selectedRequest.requested_quantity} placas ({selectedRequest.vehicle_type})
            </p>

            <form onSubmit={handleApprove}>
              {!selectedRequest.transito_id && (
                <div className="form-group">
                  <label>Organismo de Tránsito (Asignar Origen)</label>
                  <select 
                    value={approveForm.transito_id} 
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      setApproveForm({...approveForm, transito_id: selectedId, start_plate: '', end_plate: ''});
                      if (selectedId) {
                        fetchAvailablePlates(selectedId, selectedRequest.vehicle_type);
                      } else {
                        setAvailablePlates([]);
                      }
                    }}
                    required
                  >
                    <option value="">Seleccione tránsito...</option>
                    {transitos.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {selectedRequest.concesionario_id && (user?.role === 'superadmin' || !user?.transito_id) ? (
                <div className="form-group">
                  <label>Método de Asignación</label>
                  <div style={{ display: 'flex', gap: '20px', marginTop: '5px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', cursor: 'pointer' }}>
                      <input 
                        type="radio" 
                        name="approve_mode" 
                        value="existente" 
                        checked={approveForm.mode === 'existente'}
                        onChange={() => setApproveForm({...approveForm, mode: 'existente'})}
                      />
                      Asignar placas de stock existente
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'normal', cursor: 'pointer' }}>
                      <input 
                        type="radio" 
                        name="approve_mode" 
                        value="nuevo" 
                        checked={approveForm.mode === 'nuevo'}
                        onChange={() => setApproveForm({...approveForm, mode: 'nuevo'})}
                      />
                      Crear y asignar nuevo rango
                    </label>
                  </div>
                </div>
              ) : null}

              {approveForm.mode === 'existente' ? (
                <>
                  {/* Resumen de stock disponible */}
                  <div style={{ background: '#f8fafc', padding: '12px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #e2e8f0' }}>
                    <small style={{ color: '#64748b', fontWeight: '600' }}>
                      Inventario disponible libre para este Tránsito: <strong>{availablePlates.length}</strong> placas
                    </small>
                  </div>

                  {/* Lista dinámica de sub-rangos */}
                  <div style={{ marginBottom: '12px' }}>
                    {subRanges.map((sr, idx) => {
                      const count = getSubRangeCount(sr);
                      const platesForEnd = availablePlates.filter(p => !sr.start_plate || p.plate_number >= sr.start_plate);
                      return (
                        <div key={idx} style={{
                          background: '#fff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '10px',
                          padding: '14px',
                          marginBottom: '10px',
                          position: 'relative'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <span style={{ fontWeight: '700', fontSize: '13px', color: '#475569' }}>Sub-rango #{idx + 1}</span>
                            {subRanges.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeSubRange(idx)}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', lineHeight: '1', padding: '0 4px' }}
                                title="Eliminar sub-rango"
                              >🗑️</button>
                            )}
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label style={{ fontSize: '12px' }}>Placa Inicial</label>
                              <select
                                value={sr.start_plate}
                                onChange={(e) => updateSubRange(idx, 'start_plate', e.target.value)}
                                required
                              >
                                <option value="">Inicio...</option>
                                {availablePlates.map(p => (
                                  <option key={p.id} value={p.plate_number}>{p.plate_number}</option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label style={{ fontSize: '12px' }}>Placa Final</label>
                              <select
                                value={sr.end_plate}
                                onChange={(e) => updateSubRange(idx, 'end_plate', e.target.value)}
                                required
                                disabled={!sr.start_plate}
                              >
                                <option value="">Final...</option>
                                {platesForEnd.map(p => (
                                  <option key={p.id} value={p.plate_number}>{p.plate_number}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          {count > 0 && (
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#16a34a', fontWeight: '600' }}>
                              ✓ {count} placa{count !== 1 ? 's' : ''} cubierta{count !== 1 ? 's' : ''} en este sub-rango
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Botón agregar sub-rango */}
                  <button
                    type="button"
                    onClick={addSubRange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px dashed #cbd5e1',
                      borderRadius: '8px',
                      background: 'none',
                      color: '#475569',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      marginBottom: '16px',
                      transition: 'all 0.2s'
                    }}
                  >
                    ➕ Agregar sub-rango
                  </button>

                  {/* Panel de progreso */}
                  {(() => {
                    const total = getTotalCovered();
                    const requested = parseInt(selectedRequest.requested_quantity);
                    const pct = Math.min(100, Math.round((total / requested) * 100));
                    const isExact = total === requested;
                    const isOver = total > requested;
                    const isUnder = total > 0 && total < requested;
                    return (
                      <div style={{
                        background: isExact ? '#f0fdf4' : isOver ? '#fff7ed' : '#f8fafc',
                        border: `1px solid ${isExact ? '#a7f3d0' : isOver ? '#fed7aa' : '#e2e8f0'}`,
                        borderRadius: '10px',
                        padding: '14px',
                        marginBottom: '8px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', fontWeight: '700' }}>
                          <span>Total cubierto:</span>
                          <span style={{ color: isExact ? '#16a34a' : isOver ? '#d97706' : '#64748b' }}>
                            {total} / {requested} placas
                            {isExact && ' ✅'}
                            {isOver && ' ⚠️'}
                          </span>
                        </div>
                        <div style={{ background: '#e2e8f0', borderRadius: '99px', height: '8px', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${pct}%`,
                            background: isExact ? '#16a34a' : isOver ? '#f97316' : '#3b82f6',
                            borderRadius: '99px',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                        {isOver && (
                          <p style={{ color: '#b45309', fontSize: '12px', marginTop: '6px', fontWeight: '600' }}>
                            ⚠️ Estás asignando {total - requested} placas de más. Ajusta los sub-rangos.
                          </p>
                        )}
                        {isUnder && (
                          <p style={{ color: '#475569', fontSize: '12px', marginTop: '6px' }}>
                            Faltan {requested - total} placa{(requested - total) !== 1 ? 's' : ''} para completar la solicitud.
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </>

              ) : (
                <>
                  <div style={{ background: '#eff6ff', padding: '12px', borderRadius: '8px', marginBottom: '15px', border: '1px solid #bfdbfe', color: '#1e40af', fontSize: '13px' }}>
                    <strong>Generación de nuevo rango:</strong> Las placas generadas se crearán en la base de datos y quedarán asignadas {selectedRequest.concesionario_id ? 'directamente al concesionario' : 'disponibles en el stock general del tránsito'}. Puedes agregar múltiples rangos diferentes en la misma solicitud.
                  </div>

                  {/* Lista dinámica de rangos a crear */}
                  <div style={{ marginBottom: '12px' }}>
                    {newSubRanges.map((nsr, idx) => {
                      const count = getSubRangeNewCount(nsr);
                      return (
                        <div key={idx} style={{
                          background: '#fff',
                          border: '1px solid #e2e8f0',
                          borderRadius: '10px',
                          padding: '14px',
                          marginBottom: '10px',
                          position: 'relative'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <span style={{ fontWeight: '700', fontSize: '13px', color: '#475569' }}>Rango a crear #{idx + 1}</span>
                            {newSubRanges.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeNewSubRange(idx)}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '18px', lineHeight: '1', padding: '0 4px' }}
                                title="Eliminar rango"
                              >🗑️</button>
                            )}
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: selectedRequest.vehicle_type === 'moto' ? '1fr 1fr 1fr 1fr' : '1fr 1fr 1fr', gap: '8px' }}>
                            <div className="form-group" style={{ margin: 0 }}>
                              <label style={{ fontSize: '11px' }}>{selectedRequest.vehicle_type === 'moto_carro' ? 'Letras Finales' : 'Prefijo'}</label>
                              <input
                                type="text"
                                maxLength="3"
                                placeholder="ABC"
                                value={nsr.prefix}
                                onChange={(e) => updateNewSubRange(idx, 'prefix', e.target.value.toUpperCase())}
                                required
                              />
                            </div>

                            {selectedRequest.vehicle_type === 'moto' && (
                              <div className="form-group" style={{ margin: 0 }}>
                                <label style={{ fontSize: '11px' }}>Sufijo</label>
                                <input
                                  type="text"
                                  maxLength="1"
                                  placeholder="A"
                                  value={nsr.suffix}
                                  onChange={(e) => updateNewSubRange(idx, 'suffix', e.target.value.toUpperCase())}
                                  required
                                />
                              </div>
                            )}

                            <div className="form-group" style={{ margin: 0 }}>
                              <label style={{ fontSize: '11px' }}>N° Inicial</label>
                              <input
                                type="number"
                                min="0"
                                max={selectedRequest.vehicle_type === 'moto' ? 99 : 999}
                                placeholder={selectedRequest.vehicle_type === 'moto' ? '00' : '000'}
                                value={nsr.start_number}
                                onChange={(e) => updateNewSubRange(idx, 'start_number', e.target.value)}
                                required
                              />
                            </div>

                            <div className="form-group" style={{ margin: 0 }}>
                              <label style={{ fontSize: '11px' }}>N° Final</label>
                              <input
                                type="number"
                                min="0"
                                max={selectedRequest.vehicle_type === 'moto' ? 99 : 999}
                                placeholder={selectedRequest.vehicle_type === 'moto' ? '99' : '999'}
                                value={nsr.end_number}
                                onChange={(e) => updateNewSubRange(idx, 'end_number', e.target.value)}
                                required
                              />
                            </div>
                          </div>

                          {count > 0 && (
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#16a34a', fontWeight: '600' }}>
                              ✓ Se generarán {count} placa{count !== 1 ? 's' : ''} en este rango
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Botón agregar rango nuevo */}
                  <button
                    type="button"
                    onClick={addNewSubRange}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '2px dashed #cbd5e1',
                      borderRadius: '8px',
                      background: 'none',
                      color: '#475569',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: '600',
                      marginBottom: '16px',
                      transition: 'all 0.2s'
                    }}
                  >
                    ➕ Agregar otro rango nuevo
                  </button>

                  {/* Panel de progreso para modo nuevo */}
                  {(() => {
                    const total = getTotalNewCovered();
                    const requested = parseInt(selectedRequest.requested_quantity);
                    const pct = Math.min(100, Math.round((total / requested) * 100));
                    const isExact = total === requested;
                    const isOver = total > requested;
                    const isUnder = total > 0 && total < requested;
                    return (
                      <div style={{
                        background: isExact ? '#f0fdf4' : isOver ? '#fff7ed' : '#f8fafc',
                        border: `1px solid ${isExact ? '#a7f3d0' : isOver ? '#fed7aa' : '#e2e8f0'}`,
                        borderRadius: '10px',
                        padding: '14px',
                        marginBottom: '8px'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', fontWeight: '700' }}>
                          <span>Total placas a crear:</span>
                          <span style={{ color: isExact ? '#16a34a' : isOver ? '#d97706' : '#64748b' }}>
                            {total} / {requested} placas
                            {isExact && ' ✅'}
                            {isOver && ' ⚠️'}
                          </span>
                        </div>
                        <div style={{ background: '#e2e8f0', borderRadius: '99px', height: '8px', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${pct}%`,
                            background: isExact ? '#16a34a' : isOver ? '#f97316' : '#3b82f6',
                            borderRadius: '99px',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                        {isOver && (
                          <p style={{ color: '#b45309', fontSize: '12px', marginTop: '6px', fontWeight: '600' }}>
                            ⚠️ Estás creando {total - requested} placas de más sobre la cantidad solicitada.
                          </p>
                        )}
                        {isUnder && (
                          <p style={{ color: '#475569', fontSize: '12px', marginTop: '6px' }}>
                            Faltan {requested - total} placa{(requested - total) !== 1 ? 's' : ''} por definir para completar la solicitud.
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </>
              )}

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => setShowApproveModal(false)} 
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  Confirmar Aprobación
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL RECHAZAR SOLICITUD */}
      {showRejectModal && selectedRequest && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '450px' }}>
            <h3>Rechazar Solicitud #{selectedRequest.id}</h3>
            <form onSubmit={handleReject}>
              <div className="form-group">
                <label>Motivo de Rechazo</label>
                <textarea 
                  rows="4"
                  placeholder="Explica detalladamente por qué se rechaza la solicitud..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  required
                ></textarea>
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  onClick={() => setShowRejectModal(false)} 
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" style={{ background: '#dc2626', border: '1px solid #b91c1c' }}>
                  Rechazar Solicitud
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DETALLE DE PLACAS ASIGNADAS */}
      {showPlatesModal && selectedRequest && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto' }}>
            <h3>Detalle de Placas Asignadas: Solicitud #{selectedRequest.id}</h3>
            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #e2e8f0', fontSize: '13px' }}>
              <strong>Tránsito: </strong> {selectedRequest.transito_name} <br />
              <strong>Concesionario: </strong> {selectedRequest.concesionario_name || 'TransiApp (Global)'} <br />
              <strong>Tipo de Vehículo: </strong> {selectedRequest.vehicle_type === 'moto' ? '🏍️ Moto' : selectedRequest.vehicle_type === 'moto_carro' ? '🛺 Moto Carro' : '🚗 Carro'} <br />
              <strong>Cantidad Solicitada: </strong> {selectedRequest.requested_quantity} placas <br />
              {Array.isArray(selectedRequest.sub_ranges) && selectedRequest.sub_ranges.length > 0 ? (
                <div>
                  <strong>Rangos Asignados:</strong>
                  <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                    {selectedRequest.sub_ranges.map((sr, i) => (
                      <li key={i}>
                        Sub-rango #{i + 1}: <strong>{sr.start_plate}</strong> al <strong>{sr.end_plate}</strong> ({sr.count} placa{sr.count !== 1 ? 's' : ''})
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <><strong>Rango Asignado: </strong> {selectedRequest.start_plate} hasta {selectedRequest.end_plate} <br /></>
              )}
            </div>

            {loadingPlates ? (
              <p className="loading">Cargando placas...</p>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ margin: 0 }}>Placas Asignadas ({assignedPlates.length}):</h4>
                  {assignedPlates.length > 0 && (
                    <button
                      type="button"
                      onClick={() => handleDownloadPlatesExcel(selectedRequest, assignedPlates)}
                      className="btn-primary"
                      style={{ padding: '6px 12px', fontSize: '12px', background: '#16a34a', border: '1px solid #15803d' }}
                    >
                      📥 Descargar Excel
                    </button>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '8px', maxHeight: '300px', overflowY: 'auto', padding: '5px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff' }}>
                  {assignedPlates.map(p => (
                    <div 
                      key={p.id} 
                      style={{ 
                        padding: '6px', 
                        border: '1px solid #cbd5e1', 
                        borderRadius: '6px', 
                        textAlign: 'center', 
                        background: '#f8fafc',
                        fontSize: '12px'
                      }}
                    >
                      <span className="plate-badge-sm" style={{ display: 'block', fontSize: '13px', margin: '0 auto 4px auto', fontWeight: 'bold' }}>{p.plate_number}</span>
                      <span 
                        className={`status ${p.status}`} 
                        style={{ fontSize: '10px', padding: '2px 4px' }}
                      >
                        {p.status === 'disponible' ? 'Disponible' : p.status === 'asignada' ? 'Asignada' : p.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button 
                type="button" 
                onClick={() => {
                  setShowPlatesModal(false);
                  setSelectedRequest(null);
                }} 
                className="btn-secondary"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RangeRequests;
